/* Adaptable → Stages: a climb strip of the steps and their prices, then a table
   (# / Stage / Content / Price / Floor) whose rows expand to the content brief,
   photo/video limits and (paid stages) offer framing. The header folds it. */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SPolicy, SStage } from '../../Content/salesB/model';
import { OFFER_FRAMING_DEFAULT, OFFER_FRAMING_MAX_LEN } from '../../SalesLadder/offerFraming';
import { Btn, Card, Icon, NumInput, TextArea, Warn } from '../kit';
import { font } from '../style';
import { C } from '../tokens';
import { money, newStage, stageNames } from './model';

const COLS = '28px 80px minmax(0,1fr) 64px 64px 84px';

export function FoldHeader({ open, onToggle, title, hint, children }: { open: boolean; onToggle: () => void; title: string; hint?: string; children?: ReactNode }) {
  return (
    <button type="button" onClick={onToggle} className="cpt-hover-soft" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, height: 28, padding: '0 6px 0 2px', margin: '0 -2px', background: 'transparent', border: 0, borderRadius: 6, cursor: 'pointer', textAlign: 'left' }}>
      <Icon name="chevron-right" size={14} color={C.purpleLt} style={{ flex: 'none', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      <span style={{ font: font(600, 13), color: C.text, whiteSpace: 'nowrap' }}>{title}</span>
      {hint && <span style={{ font: font(400, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{hint}</span>}
      {children}
    </button>
  );
}

const num = (paid: boolean) => ({ width: 22, height: 22, borderRadius: 6, background: paid ? C.tint20 : C.hover, color: paid ? C.purplePale : C.sub, font: font(600, 11), display: 'flex', alignItems: 'center', justifyContent: 'center' } as const);

export function StagesSection({ s, set, open, onToggle }: { s: SPolicy; set: (stages: SStage[]) => void; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [exp, setExp] = useState<string | null>(null);
  const st = s.stages;
  const names = stageNames(st, t);
  const paid = st.filter((x) => x.kind === 'sale');
  const prices = paid.map((x) => x.price).filter(Boolean);
  const range = prices.length ? `${money(prices[0])} → ${money(prices[prices.length - 1])}` : t('sl.st.noPrices');
  const noPrice = paid.filter((x) => !x.price).length;

  const edit = (i: number, p: Partial<SStage>) => set(st.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= st.length) return;
    const n = [...st];
    [n[i], n[j]] = [n[j]!, n[i]!];
    set(n);
  };
  const add = (kind: 'free' | 'sale') => {
    const n = newStage(kind);
    set([...st, n]);
    setExp(n.id);
  };

  return (
    <div data-tutorial="sl-stages" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex' }}>
        <FoldHeader open={open} onToggle={onToggle} title={t('sl.st.title')} hint={t('sl.st.hint')}>
          <span style={{ flex: 1 }} />
          <span style={{ font: font(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{t('sl.st.count', { count: st.length })} ·</span>
          <span style={{ font: font(600, 12), color: C.purplePale, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{range}</span>
        </FoldHeader>
      </div>
      {open && (
        <Card>
          <div className="cpt-thin-scroll" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', overflowX: 'auto' }}>
            {st.map((x, k) => (
              <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, height: 24, padding: '0 9px', borderRadius: 99, background: x.kind === 'sale' ? C.tint16 : C.hover, font: font(500, 12), color: x.kind === 'sale' ? C.purplePale : C.sub, whiteSpace: 'nowrap' }}>
                  {names[k]}
                  <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{x.kind === 'sale' ? money(x.price) || '—' : t('sl.free')}</span>
                </span>
                {k < st.length - 1 && <Icon name="chevron-right" size={13} color={C.faint} />}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, padding: '7px 14px', font: font(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
            <span />
            <span>{t('sl.st.colStage')}</span>
            <span>{t('sl.st.colContent')}</span>
            <span>{t('sl.st.colPrice')}</span>
            <span>{t('sl.st.colFloor')}</span>
            <span />
          </div>
          {st.map((x, k) => {
            const o = exp === x.id;
            const sale = x.kind === 'sale';
            return (
              <div key={x.id} style={{ background: o ? 'var(--om-panel)' : 'transparent' }}>
                <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', padding: '6px 14px' }}>
                  <span style={num(sale)}>{k + 1}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span style={{ font: font(500, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{names[k]}</span>
                    <span style={{ font: font(400, 11), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sale ? t('sl.st.paid') : t('sl.st.hook')}</span>
                  </div>
                  <span onClick={() => setExp(o ? null : x.id)} title={x.guidance} style={{ font: font(400, 12), color: x.guidance ? C.sub : C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{x.guidance || t('sl.st.noContent')}</span>
                  {sale ? (
                    <>
                      <NumInput value={x.price || null} onChange={(v) => edit(k, { price: v ?? 0 })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" />
                      <NumInput value={x.floor || null} onChange={(v) => edit(k, { floor: v ?? 0 })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" />
                    </>
                  ) : <span style={{ gridColumn: 'span 2', font: font(500, 12), color: C.purplePale, paddingLeft: 8 }}>{t('sl.free')}</span>}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <IconBtn icon="arrow-up" title={t('sl.moveUp')} off={!k} onClick={() => move(k, -1)} />
                    <IconBtn icon="arrow-down" title={t('sl.moveDown')} off={k === st.length - 1} onClick={() => move(k, 1)} />
                    <IconBtn icon="chevron-down" title={t('sl.st.details')} onClick={() => setExp(o ? null : x.id)} rot={o} color={C.sub} />
                    <IconBtn icon="x" title={t('sl.st.remove')} onClick={() => { set(st.filter((_, j) => j !== k)); if (o) setExp(null); }} />
                  </div>
                </div>
                {o && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 14px 14px 52px' }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ font: font(500, 12), color: 'var(--om-body)' }}>{t('sl.st.send')}</span>
                        <TextArea value={x.guidance} onChange={(v) => edit(k, { guidance: v })} placeholder={t('sl.st.sendPh')} height={72} />
                      </div>
                      <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span title={t('sl.st.limitsTip')} style={{ font: font(500, 12), color: 'var(--om-body)' }}>{t('sl.st.limits')} <span style={{ fontWeight: 400, color: C.muted }}>· {t('sl.st.limitsHint')}</span></span>
                        {(['photos', 'videos'] as const).map((m) => (
                          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, font: font(400, 12), color: C.muted }}>
                            <span style={{ width: 48, color: C.sub }}>{t(`sl.st.${m}`)}</span>
                            <NumInput value={m === 'photos' ? x.photosMin : x.videosMin} onChange={(v) => edit(k, m === 'photos' ? { photosMin: v } : { videosMin: v })} min={0} max={50} int allowEmpty placeholder={t('sl.st.min')} width={48} />
                            <span>–</span>
                            <NumInput value={m === 'photos' ? x.photosMax : x.videosMax} onChange={(v) => edit(k, m === 'photos' ? { photosMax: v } : { videosMax: v })} min={0} max={50} int allowEmpty placeholder={t('sl.st.max')} width={48} />
                          </div>
                        ))}
                      </div>
                    </div>
                    {sale && <Framing value={x.offerFraming} onChange={(v) => edit(k, { offerFraming: v })} />}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', flexWrap: 'wrap' }}>
            <Btn kind="ghost" icon="plus" onClick={() => add('free')}>{t('sl.st.addTease')}</Btn>
            <Btn kind="tint" icon="plus" onClick={() => add('sale')}>{t('sl.st.addPaid')}</Btn>
            <span style={{ flex: 1 }} />
            {noPrice > 0 && <Warn>{t('sl.st.needPrice', { count: noPrice })}</Warn>}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Offer-framing textarea with its hint and an n/800 counter. */
export function Framing({ value, onChange, title, hint, height = 72 }: { value: string; onChange: (v: string) => void; title?: string; hint?: string; height?: number }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: font(500, title ? 13 : 12), color: title ? C.text : 'var(--om-body)' }}>{title ?? t('sl.framing')}</span>
        <span style={{ font: font(400, 11.5), color: C.muted }}>{hint ?? t('sl.framingHint')}</span>
        <span style={{ flex: 1 }} />
        <span style={{ font: font(400, 11), color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{value.length}/{OFFER_FRAMING_MAX_LEN}</span>
      </div>
      <TextArea value={value} onChange={(v) => onChange(v.slice(0, OFFER_FRAMING_MAX_LEN))} placeholder={OFFER_FRAMING_DEFAULT} height={height} />
    </div>
  );
}

export function IconBtn({ icon, title, onClick, off, rot, color, active }: { icon: string; title: string; onClick: () => void; off?: boolean; rot?: boolean; color?: string; active?: boolean }) {
  return (
    <button type="button" title={title} disabled={off} onClick={onClick} className={off ? undefined : 'cpt-hover'} style={{ width: 22, height: 24, flex: 'none', padding: 0, background: 'transparent', border: 0, borderRadius: 5, color: active ? C.purpleLt : color ?? C.muted, cursor: off ? 'default' : 'pointer', opacity: off ? 0.3 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={icon} size={13} style={{ transform: rot ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
    </button>
  );
}
